import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { MaintenanceView } from '@/components/MaintenanceView'
import { API_BASE_URL } from '@/lib/apiBaseUrl'
import {
  getStorefrontSiteConfig,
  getStorefrontRotatingSearchTerms,
  type StorefrontCategoryModel,
  type StorefrontSiteConfig,
} from '@/lib/api-shell'
import { markBackendAvailable, markBackendUnavailable } from '@/lib/backend-availability'
import {
  assertStorefrontConfigIdentity,
  buildStorefrontConfigCacheScope,
  resolveExpectedStorefrontConfigIdentity,
} from '@/lib/storefront-config-identity'
import {
  configureStorefrontPublicPricing,
  resolveStorefrontPublicPricingEnabled,
} from '@/lib/storefront-public-pricing'
import {
  isDeploySnapshotStorefront,
  readStorefrontSiteConfigSnapshot,
  shouldShowStorefrontSiteConfigLoading,
} from '@/lib/storefront-site-config-snapshot'
import { isHexColor, normalizeThemeColor, resolveContrastColor } from '@/lib/storefront-theme'
import { resolveStorefrontSitePresentation } from '@/lib/storefront-site-presentation'

type StorefrontSocialLink = {
  key: string
  label: string
  url: string
}

type StorefrontSearchHighlight = {
  label: string
  badge: string
}

type StorefrontThemeColors = {
  launches: string
  justArrived: string
  priceCard: string
  priceText: string
  buyButton: string
  buyButtonForeground: string
  addToCartButton: string
  addToCartButtonForeground: string
  largePhotoBackground: string
}

type StorefrontSiteConfigContextValue = {
  siteConfig: StorefrontSiteConfig | null
  isLoading: boolean
  error: Error | null
  siteName: string
  seoTitle: string
  seoDescription: string
  categoryModel: StorefrontCategoryModel
  primaryColor: string
  secondaryColor: string
  themeColors: StorefrontThemeColors
  showPricesBeforeLogin: boolean | null
  showAppsMenu: boolean
  showManufacturerBrandFilter: boolean
  showVehiclePlateFilter: boolean
  useAutoPartsPlaceholder: boolean
  showBrandCarousel: boolean
  socialLinks: StorefrontSocialLink[]
  popularSearches: string[]
  searchHighlights: StorefrontSearchHighlight[]
  institutionalTexts: Record<string, string>
  checkoutAttemptRecoveryEnabled: boolean
  siteConfigCacheTtlMs: number
  isDeploySnapshot: boolean
  refreshSiteConfig: (options?: { forceBypassCache?: boolean }) => Promise<void>
}

const StorefrontSiteConfigContext = createContext<StorefrontSiteConfigContextValue | null>(null)

const DEFAULT_PRICE_CARD_COLOR = '#e6f6ee'
const DEFAULT_PRICE_TEXT_COLOR = '#008a45'
const DEFAULT_BUY_BUTTON_COLOR = '#2563eb'
const DEFAULT_ADD_TO_CART_BUTTON_COLOR = '#1d4ed8'
const DEFAULT_LARGE_PHOTO_BACKGROUND_COLOR = '#e9e9ef'
const BOOTSTRAP_THEME_PRIMARY_COLOR = '#f3f4f6'
const BOOTSTRAP_THEME_SECONDARY_COLOR = '#e5e7eb'
const MANAGED_SCRIPT_ATTR = 'data-storefront-site-script'
const MANAGED_FAVICON_ATTR = 'data-storefront-site-favicon'
const SITE_CONFIG_CACHE_STORAGE_VERSION = 7
const SITE_CONFIG_CACHE_STORAGE_KEY_PREFIX = `storefront-site-config-cache-v${SITE_CONFIG_CACHE_STORAGE_VERSION}`
const SITE_CONFIG_REQUEST_TIMEOUT_MS = 8_000
const DEFAULT_SITE_CONFIG_CACHE_TTL_MS = 5 * 60 * 1000
const MAX_SITE_CONFIG_CACHE_TTL_MS = 24 * 60 * 60 * 1000
const FAVICON_LINK_SELECTOR = [
  'link[rel="icon"]',
  'link[rel="shortcut icon"]',
  'link[rel="apple-touch-icon"]',
].join(', ')

type CachedStorefrontSiteConfigEntry = {
  version: number
  scope: string
  cachedAt: number
  config: StorefrontSiteConfig
}

const normalizeText = (value: unknown) => String(value ?? '').trim()

const normalizeTruthyFlag = (value: string | null | undefined) => ['1', 'true', 'yes', 'on', 'sim'].includes(
  normalizeText(value).toLowerCase()
)

const toRecord = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return value as Record<string, unknown>
}

const normalizeOptionalNonNegativeInteger = (value: unknown) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0) return null
  return Math.trunc(parsed)
}

const extractSiteConfigErrorStatus = (error: Error) => {
  const match = error.message.match(/\bsite_config_request_failed:(\d{3})\b/i)
  if (!match) return null

  const parsed = Number.parseInt(match[1] ?? '', 10)
  return Number.isFinite(parsed) ? parsed : null
}

const shouldMarkSiteConfigFailureAsBackendUnavailable = (error: Error) => {
  if (
    error.message === 'site_config_timeout'
    || error.message === 'site_config_identity_missing'
    || error.message === 'site_config_identity_mismatch'
  ) {
    return true
  }

  const status = extractSiteConfigErrorStatus(error)
  if (status === null) {
    return false
  }

  return status >= 500
}

const resolveSiteConfigCacheTtlMs = (config: StorefrontSiteConfig | null | undefined) => {
  const apiTtlMs = normalizeOptionalNonNegativeInteger(config?.checkout_config?.site_config_cache_ttl_ms)
  const envTtlMs = normalizeOptionalNonNegativeInteger(import.meta.env.VITE_SITE_CONFIG_CACHE_TTL_MS)
  const ttlMs = apiTtlMs ?? envTtlMs ?? DEFAULT_SITE_CONFIG_CACHE_TTL_MS
  return Math.max(0, Math.min(MAX_SITE_CONFIG_CACHE_TTL_MS, ttlMs))
}

const shouldForceSiteConfigRefreshFromLocation = () => {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return (
    normalizeTruthyFlag(params.get('refresh_site_config'))
    || normalizeTruthyFlag(params.get('site_config_refresh'))
  )
}

const normalizeInstitutionalTexts = (value: unknown) =>
  Object.fromEntries(
    Object.entries(toRecord(value))
      .map(([key, entry]) => [key, normalizeText(entry)])
      .filter(([, entry]) => entry)
  ) as Record<string, string>

const normalizeStringList = (value: unknown) => {
  if (!Array.isArray(value)) return []
  return value
    .map((entry) => normalizeText(entry))
    .filter(Boolean)
}

const normalizeSocialLinks = (value: unknown): StorefrontSocialLink[] => {
  const labels: Record<string, string> = {
    facebook: 'Facebook',
    youtube: 'YouTube',
    instagram: 'Instagram',
    x: 'X',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn',
  }

  const links = Object.entries(labels)
    .map(([key, label]) => {
      const root = toRecord(value)
      const entry = key === 'x' ? (root.x ?? root.twitter) : root[key]
      const entryRecord = toRecord(entry)
      const resolvedUrl = normalizeText(entryRecord.url ?? entryRecord.href ?? entryRecord.link ?? entry)
      const activeValue = entryRecord.ativo ?? (
        key === 'x'
          ? (root.x_ativo ?? root.twitter_ativo)
          : root[`${key}_ativo`]
      )
      const active = activeValue === undefined
        ? Boolean(resolvedUrl)
        : !['0', 'false', 'nao', 'não', 'off', 'inactive'].includes(normalizeText(activeValue).toLowerCase())

      if (!resolvedUrl || !active) return null
      return { key, label, url: resolvedUrl }
    })
    .filter((entry): entry is StorefrontSocialLink => Boolean(entry))

  return links
}

const normalizeSearchHighlights = (value: unknown): StorefrontSearchHighlight[] => {
  if (!Array.isArray(value)) return []

  return value
    .map((entry) => {
      const entryRecord = toRecord(entry)
      const label = normalizeText(entryRecord.termo ?? entryRecord.term ?? entryRecord.label ?? entryRecord.text ?? entryRecord.value ?? entry)
      const badge = normalizeText(entryRecord.badge ?? entryRecord.badge_label ?? entryRecord.tag ?? entryRecord.status)
      if (!label) return null
      return { label, badge }
    })
    .filter((entry): entry is StorefrontSearchHighlight => Boolean(entry))
}

const normalizeSearchConfig = (value: unknown) => {
  const record = toRecord(value)
  return {
    popularSearches: normalizeStringList(record.buscas_populares ?? record.populares),
    searchHighlights: normalizeSearchHighlights(record.termos_rotativos ?? record.destaques),
  }
}

const normalizeCategoryModel = (value: unknown): StorefrontCategoryModel =>
  Number(value ?? 0) === 1 ? 'classes' : 'grupos'

const resolveExpectedSiteConfigIdentity = () => {
  try {
    return resolveExpectedStorefrontConfigIdentity()
  } catch {
    return null
  }
}

const resolveSiteConfigCacheScope = () => {
  const expected = resolveExpectedSiteConfigIdentity()
  if (expected) {
    return buildStorefrontConfigCacheScope({
      origin: typeof window === 'undefined' ? '' : window.location.origin,
      expected,
    })
  }

  return `legacy|${encodeURIComponent(typeof window === 'undefined' ? '' : window.location.origin)}`
}

const resolveSiteConfigCacheStorageKey = () => {
  const expected = resolveExpectedSiteConfigIdentity()
  if (!expected) {
    return [
      SITE_CONFIG_CACHE_STORAGE_KEY_PREFIX,
      'legacy',
      encodeURIComponent(typeof window === 'undefined' ? '' : window.location.origin),
    ].join(':')
  }

  return [
    SITE_CONFIG_CACHE_STORAGE_KEY_PREFIX,
    `v${expected.version}`,
    expected.integrationServer,
    expected.lojaId,
    encodeURIComponent(expected.publicUrl),
  ].join(':')
}

const readCachedSiteConfigEntry = (): CachedStorefrontSiteConfigEntry | null => {
  if (typeof window === 'undefined') return null

  const storageKey = resolveSiteConfigCacheStorageKey()
  try {
    if (shouldForceSiteConfigRefreshFromLocation()) {
      window.localStorage.removeItem(storageKey)
      return null
    }

    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return null

    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }

    const entrySource = parsed as Partial<CachedStorefrontSiteConfigEntry> & Record<string, unknown>
    const config = (
      entrySource.config
      && typeof entrySource.config === 'object'
      && !Array.isArray(entrySource.config)
        ? entrySource.config
        : parsed
    ) as StorefrontSiteConfig
    const cachedAt = typeof entrySource.cachedAt === 'number' && Number.isFinite(entrySource.cachedAt)
      ? Math.trunc(entrySource.cachedAt)
      : 0
    const scope = normalizeText(entrySource.scope)
    const expectedScope = resolveSiteConfigCacheScope()

    const ttlMs = resolveSiteConfigCacheTtlMs(config)
    if (
      entrySource.version !== SITE_CONFIG_CACHE_STORAGE_VERSION
      || scope !== expectedScope
      || !cachedAt
      || ttlMs === 0
      || Date.now() - cachedAt > ttlMs
    ) {
      window.localStorage.removeItem(storageKey)
      return null
    }

    const expected = resolveExpectedSiteConfigIdentity()
    if (expected) assertStorefrontConfigIdentity(config, expected)

    return {
      version: SITE_CONFIG_CACHE_STORAGE_VERSION,
      scope,
      cachedAt,
      config,
    }
  } catch {
    window.localStorage.removeItem(storageKey)
    return null
  }
}

const writeCachedSiteConfig = (value: StorefrontSiteConfig | null) => {
  if (typeof window === 'undefined') return

  const storageKey = resolveSiteConfigCacheStorageKey()
  try {
    if (!value) {
      window.localStorage.removeItem(storageKey)
      return
    }

    const expected = resolveExpectedSiteConfigIdentity()
    if (expected) assertStorefrontConfigIdentity(value, expected)
    const payload: CachedStorefrontSiteConfigEntry = {
      version: SITE_CONFIG_CACHE_STORAGE_VERSION,
      scope: resolveSiteConfigCacheScope(),
      cachedAt: Date.now(),
      config: value,
    }
    window.localStorage.setItem(storageKey, JSON.stringify(payload))
  } catch {
    // Ignore storage quota and privacy errors.
  }
}

const cloneManagedScriptNode = (node: ChildNode) => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent?.trim()
    return text ? document.createTextNode(text) : null
  }

  if (!(node instanceof HTMLElement)) return null

  if (node.tagName.toLowerCase() === 'script') {
    const script = document.createElement('script')
    Array.from(node.attributes).forEach((attribute) => script.setAttribute(attribute.name, attribute.value))
    script.text = node.textContent ?? ''
    script.setAttribute(MANAGED_SCRIPT_ATTR, 'true')
    return script
  }

  const clone = node.cloneNode(true)
  if (clone instanceof HTMLElement) {
    clone.setAttribute(MANAGED_SCRIPT_ATTR, 'true')
  }
  return clone
}

const removeManagedNodes = (selector: string) => {
  if (typeof document === 'undefined') return
  document.querySelectorAll(selector).forEach((node) => node.remove())
}

const normalizeFaviconUrl = (value: string) => {
  if (!value) return ''
  const separator = value.includes('?') ? '&' : '?'
  return `${value}${separator}storefront_favicon=1`
}

async function fetchStorefrontSiteConfigDirect(
  options: {
    lojaId?: string | number | null
    signal?: AbortSignal
    cache?: RequestCache
    headers?: HeadersInit
    cacheBust?: string | number | boolean | null | undefined
  } = {}
): Promise<StorefrontSiteConfig> {
  return getStorefrontSiteConfig(options.lojaId, {
    signal: options.signal,
    cache: options.cache,
    headers: options.headers,
    cacheBust: options.cacheBust ?? undefined,
  })
}

export function StorefrontSiteConfigProvider({ children }: { children: ReactNode }) {
  const initialSiteConfigRef = useRef<{
    isDeploySnapshot: boolean
    config: StorefrontSiteConfig | null
    error: Error | null
  } | null>(null)
  if (initialSiteConfigRef.current === null) {
    const isDeploySnapshot = isDeploySnapshotStorefront()
    try {
      const config = isDeploySnapshot ? readStorefrontSiteConfigSnapshot() : null
      configureStorefrontPublicPricing(config)
      initialSiteConfigRef.current = { isDeploySnapshot, config, error: null }
    } catch (snapshotError) {
      initialSiteConfigRef.current = {
        isDeploySnapshot,
        config: null,
        error: snapshotError instanceof Error
          ? snapshotError
          : new Error('storefront_site_config_snapshot_invalid'),
      }
    }
  }
  const initialSiteConfig = initialSiteConfigRef.current
  const isDeploySnapshot = initialSiteConfig.isDeploySnapshot

  const [siteConfig, setSiteConfig] = useState<StorefrontSiteConfig | null>(
    initialSiteConfig.config
  )
  const [isLoading, setIsLoading] = useState(!isDeploySnapshot)
  const [error, setError] = useState<Error | null>(initialSiteConfig.error)
  const [dynamicSearchHighlights, setDynamicSearchHighlights] = useState<StorefrontSearchHighlight[]>([])
  const isMountedRef = useRef(true)
  const siteConfigRef = useRef<StorefrontSiteConfig | null>(initialSiteConfig.config)
  const siteConfigRequestRef = useRef<Promise<void> | null>(null)
  const siteConfigAbortControllerRef = useRef<AbortController | null>(null)
  const dynamicSearchHighlightsRequestRef = useRef<Promise<void> | null>(null)
  const lastSuccessfulSiteConfigFetchAtRef = useRef(0)

  const refreshSiteConfig = useCallback(async (options: { forceBypassCache?: boolean } = {}) => {
    if (isDeploySnapshot) return

    if (siteConfigRequestRef.current) {
      return siteConfigRequestRef.current
    }

    let didTimeout = false
    let timeoutId: number | null = null
    const controller = typeof AbortController === 'undefined' ? null : new AbortController()
    siteConfigAbortControllerRef.current = controller

    if (controller && typeof window !== 'undefined') {
      timeoutId = window.setTimeout(() => {
        didTimeout = true
        controller.abort()
      }, SITE_CONFIG_REQUEST_TIMEOUT_MS)
    }

    const request = fetchStorefrontSiteConfigDirect({
      signal: controller?.signal,
      cache: 'no-store',
      headers: {
        'cache-control': 'no-cache, no-store, max-age=0',
        pragma: 'no-cache',
      },
      cacheBust: options.forceBypassCache || shouldForceSiteConfigRefreshFromLocation()
        ? Date.now()
        : undefined,
    })
      .then((response) => {
        if (!isMountedRef.current) return
        configureStorefrontPublicPricing(response)
        siteConfigRef.current = response
        setSiteConfig(response)
        writeCachedSiteConfig(response)
        lastSuccessfulSiteConfigFetchAtRef.current = Date.now()
        setError(null)
        markBackendAvailable()
      })
      .catch((requestError) => {
        if (!isMountedRef.current) return
        const normalizedError = didTimeout
          ? new Error('site_config_timeout')
          : requestError instanceof Error
            ? requestError
            : new Error('site_config_failed')
        if (!siteConfigRef.current && shouldMarkSiteConfigFailureAsBackendUnavailable(normalizedError)) {
          markBackendUnavailable(normalizedError, { immediate: true })
        }
        setError(normalizedError)
      })
      .finally(() => {
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId)
        }
        if (siteConfigAbortControllerRef.current === controller) {
          siteConfigAbortControllerRef.current = null
        }
        siteConfigRequestRef.current = null
        if (isMountedRef.current) {
          setIsLoading(false)
        }
      })

    siteConfigRequestRef.current = request
    return request
  }, [isDeploySnapshot])

  useEffect(() => () => {
    isMountedRef.current = false
    siteConfigAbortControllerRef.current?.abort()
  }, [])

  useEffect(() => {
    if (isDeploySnapshot) return
    void refreshSiteConfig()
  }, [isDeploySnapshot, refreshSiteConfig])

  const refreshDynamicSearchHighlights = useCallback(async () => {
    if (dynamicSearchHighlightsRequestRef.current) {
      return dynamicSearchHighlightsRequestRef.current
    }

    const request = getStorefrontRotatingSearchTerms()
      .then((response) => {
        if (!isMountedRef.current) return
        setDynamicSearchHighlights(
          (response?.termos ?? []).map((term) => ({
            label: term.label,
            badge: '',
          }))
        )
      })
      .catch(() => {
        if (!isMountedRef.current) return
        setDynamicSearchHighlights([])
      })
      .finally(() => {
        dynamicSearchHighlightsRequestRef.current = null
      })

    dynamicSearchHighlightsRequestRef.current = request
    return request
  }, [])

  useEffect(() => {
    void refreshDynamicSearchHighlights()
  }, [refreshDynamicSearchHighlights])

  useEffect(() => {
    if (isDeploySnapshot) return undefined
    if (typeof window === 'undefined') return undefined

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== resolveSiteConfigCacheStorageKey() || !event.newValue) return
      const nextEntry = readCachedSiteConfigEntry()
      if (!nextEntry) return
      lastSuccessfulSiteConfigFetchAtRef.current = nextEntry.cachedAt
      siteConfigRef.current = nextEntry.config
      setSiteConfig(nextEntry.config)
      setError(null)
      markBackendAvailable()
    }

    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [isDeploySnapshot])

  useEffect(() => {
    if (isDeploySnapshot) return undefined
    if (typeof document === 'undefined') return undefined

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      void refreshSiteConfig({ forceBypassCache: true })
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isDeploySnapshot, refreshSiteConfig])

  const institutionalTexts = useMemo(
    () => normalizeInstitutionalTexts(siteConfig?.textos_institucionais),
    [siteConfig?.textos_institucionais]
  )
  const socialLinks = useMemo(
    () => normalizeSocialLinks(siteConfig?.redes_sociais),
    [siteConfig?.redes_sociais]
  )
  const searchConfig = useMemo(
    () => normalizeSearchConfig(siteConfig?.busca_config),
    [siteConfig?.busca_config]
  )

  const sitePresentation = useMemo(
    () => resolveStorefrontSitePresentation(siteConfig),
    [siteConfig]
  )
  const siteName = sitePresentation.siteName
  const categoryModel = normalizeCategoryModel(siteConfig?.modelo_categoria)
  const primaryColor = sitePresentation.primaryColor || BOOTSTRAP_THEME_PRIMARY_COLOR
  const secondaryColor = sitePresentation.secondaryColor || BOOTSTRAP_THEME_SECONDARY_COLOR
  const themeColors = useMemo<StorefrontThemeColors>(() => {
    const launches = sitePresentation.launchesColor || primaryColor
    const justArrived = sitePresentation.justArrivedColor || secondaryColor
    const priceCard = normalizeThemeColor(siteConfig?.cor_card_preco, DEFAULT_PRICE_CARD_COLOR)
    const priceText = normalizeThemeColor(siteConfig?.cor_preco, DEFAULT_PRICE_TEXT_COLOR)
    const buyButton = normalizeThemeColor(siteConfig?.cor_botao_comprar, DEFAULT_BUY_BUTTON_COLOR)
    const addToCartButton = normalizeThemeColor(
      siteConfig?.cor_botao_adicionar_carrinho,
      DEFAULT_ADD_TO_CART_BUTTON_COLOR
    )
    const largePhotoBackground = normalizeThemeColor(
      siteConfig?.cor_fundo_foto_grande,
      DEFAULT_LARGE_PHOTO_BACKGROUND_COLOR
    )

    return {
      launches,
      justArrived,
      priceCard,
      priceText,
      buyButton,
      buyButtonForeground: resolveContrastColor(buyButton),
      addToCartButton,
      addToCartButtonForeground: resolveContrastColor(addToCartButton),
      largePhotoBackground,
    }
  }, [primaryColor, secondaryColor, siteConfig, sitePresentation.justArrivedColor, sitePresentation.launchesColor])
  const seoTitle = sitePresentation.seoTitle
  const seoDescription = sitePresentation.seoDescription
  const showPricesBeforeLogin = siteConfig
    ? resolveStorefrontPublicPricingEnabled(siteConfig)
    : false
  const showAppsMenu = siteConfig ? Boolean(siteConfig.mostrar_menu_aplicativos) : true
  const showManufacturerBrandFilter = siteConfig ? Boolean(siteConfig.filtrar_marcas_busca) : false
  const isAutoPartsStorefront = siteConfig ? Number(siteConfig.tipo ?? 0) === 1 : false
  const showVehiclePlateFilter = isAutoPartsStorefront && Boolean(siteConfig?.filtrar_placa_modelo_busca)
  const showBrandCarousel = siteConfig ? Boolean(siteConfig.exibir_carrossel_marcas) : false
  const checkoutAttemptRecoveryEnabled = siteConfig?.checkout_config?.attempt_recovery_enabled !== false
  const siteConfigCacheTtlMs = resolveSiteConfigCacheTtlMs(siteConfig)

  useEffect(() => {
    configureStorefrontPublicPricing(siteConfig)
  }, [siteConfig])

  useEffect(() => {
    if (typeof document === 'undefined') return
    removeManagedNodes(`[${MANAGED_SCRIPT_ATTR}="true"]`)

    const scriptsMarkup = normalizeText(siteConfig?.scripts)
    if (!scriptsMarkup) return

    const template = document.createElement('template')
    template.innerHTML = scriptsMarkup

    Array.from(template.content.childNodes).forEach((node) => {
      const managedNode = cloneManagedScriptNode(node)
      if (!managedNode) return

      if (managedNode instanceof HTMLLinkElement || managedNode instanceof HTMLStyleElement) {
        document.head.appendChild(managedNode)
        return
      }

      document.body.appendChild(managedNode)
    })

    return () => {
      removeManagedNodes(`[${MANAGED_SCRIPT_ATTR}="true"]`)
    }
  }, [siteConfig?.scripts])

  useEffect(() => {
    if (typeof document === 'undefined') return
    removeManagedNodes(`link[${MANAGED_FAVICON_ATTR}="true"]`)

    const faviconUrl = normalizeText(siteConfig?.favicon_url)
    if (!faviconUrl) return
    const resolvedFaviconUrl = normalizeFaviconUrl(faviconUrl)

    document.querySelectorAll(FAVICON_LINK_SELECTOR).forEach((node) => node.remove())

    ;['icon', 'shortcut icon', 'apple-touch-icon'].forEach((rel) => {
      const link = document.createElement('link')
      link.rel = rel
      link.href = resolvedFaviconUrl
      link.setAttribute(MANAGED_FAVICON_ATTR, 'true')
      if (rel === 'icon') {
        link.type = resolvedFaviconUrl.toLowerCase().includes('.ico')
          ? 'image/x-icon'
          : 'image/png'
      }
      document.head.appendChild(link)
    })
  }, [siteConfig?.favicon_url])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const rootStyle = document.documentElement.style
    const nextPrimaryColor = isLoading && !siteConfig
      ? BOOTSTRAP_THEME_PRIMARY_COLOR
      : isHexColor(primaryColor)
        ? primaryColor
        : BOOTSTRAP_THEME_PRIMARY_COLOR
    const nextPrimaryForeground = resolveContrastColor(nextPrimaryColor)
    const nextSecondaryColor = isLoading && !siteConfig
      ? BOOTSTRAP_THEME_SECONDARY_COLOR
      : isHexColor(secondaryColor)
        ? secondaryColor
        : BOOTSTRAP_THEME_SECONDARY_COLOR
    const nextSecondaryForeground = resolveContrastColor(nextSecondaryColor)

    rootStyle.setProperty('--primary', nextPrimaryColor)
    rootStyle.setProperty('--primary-foreground', nextPrimaryForeground)
    rootStyle.setProperty('--accent', nextSecondaryColor)
    rootStyle.setProperty('--accent-foreground', nextSecondaryForeground)
    rootStyle.setProperty('--header-bg', nextPrimaryColor)
    rootStyle.setProperty('--header-foreground', nextPrimaryForeground)
    rootStyle.setProperty('--ring', nextPrimaryColor)
    rootStyle.setProperty('--store-price-card-bg', themeColors.priceCard)
    rootStyle.setProperty('--store-price-text', themeColors.priceText)
    rootStyle.setProperty('--store-buy-button-bg', themeColors.buyButton)
    rootStyle.setProperty('--store-buy-button-fg', themeColors.buyButtonForeground)
    rootStyle.setProperty('--store-cart-button-bg', themeColors.addToCartButton)
    rootStyle.setProperty('--store-cart-button-fg', themeColors.addToCartButtonForeground)
    rootStyle.setProperty('--store-large-photo-bg', themeColors.largePhotoBackground)
  }, [isLoading, primaryColor, secondaryColor, siteConfig, themeColors])

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.dataset.checkoutAttemptRecovery = checkoutAttemptRecoveryEnabled ? 'enabled' : 'disabled'
  }, [checkoutAttemptRecoveryEnabled])

  const value = useMemo<StorefrontSiteConfigContextValue>(() => ({
    siteConfig,
    isLoading,
    error,
    siteName,
    seoTitle,
    seoDescription,
    categoryModel,
    primaryColor,
    secondaryColor,
    themeColors,
    showPricesBeforeLogin,
    showAppsMenu,
    showManufacturerBrandFilter,
    showVehiclePlateFilter,
    useAutoPartsPlaceholder: isAutoPartsStorefront,
    showBrandCarousel,
    socialLinks,
    popularSearches: searchConfig.popularSearches,
    searchHighlights: dynamicSearchHighlights.length > 0 ? dynamicSearchHighlights : searchConfig.searchHighlights,
    institutionalTexts,
    checkoutAttemptRecoveryEnabled,
    siteConfigCacheTtlMs,
    isDeploySnapshot,
    refreshSiteConfig,
  }), [
    checkoutAttemptRecoveryEnabled,
    error,
    refreshSiteConfig,
    institutionalTexts,
    isLoading,
    isDeploySnapshot,
    dynamicSearchHighlights,
    isAutoPartsStorefront,
    searchConfig.popularSearches,
    searchConfig.searchHighlights,
    primaryColor,
    categoryModel,
    secondaryColor,
    themeColors,
    seoDescription,
    seoTitle,
    showAppsMenu,
    showBrandCarousel,
    showManufacturerBrandFilter,
    showVehiclePlateFilter,
    showPricesBeforeLogin,
    siteConfig,
    siteConfigCacheTtlMs,
    siteName,
    socialLinks,
  ])

  return (
    <StorefrontSiteConfigContext.Provider value={value}>
      {children}
    </StorefrontSiteConfigContext.Provider>
  )
}

export const useStorefrontSiteConfig = () => {
  const context = useContext(StorefrontSiteConfigContext)
  if (!context) {
    throw new Error('useStorefrontSiteConfig must be used within StorefrontSiteConfigProvider')
  }
  return context
}

export function StorefrontSiteConfigGate({ children }: { children: ReactNode }) {
  const context = useStorefrontSiteConfig()
  const shouldBypassSiteConfigGateInDev = import.meta.env.DEV && (context.error || !context.siteConfig)

  // Contract v1 already receives a validated deploy snapshot before React
  // starts. Never hide the storefront behind the legacy API loading screen.
  if (!shouldBypassSiteConfigGateInDev && shouldShowStorefrontSiteConfigLoading(context.isLoading, context.isDeploySnapshot)) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background" aria-live="polite">
        <div className="h-5 w-44 animate-pulse rounded bg-muted" aria-label="Carregando loja" />
      </main>
    )
  }

  if (!shouldBypassSiteConfigGateInDev && (context.error || !context.siteConfig)) {
    return (
      <MaintenanceView
        variant="unavailable"
        onRetry={() => {
          if (context.isDeploySnapshot) {
            window.location.reload()
            return
          }
          void context.refreshSiteConfig({ forceBypassCache: true })
        }}
      />
    )
  }

  return <>{children}</>
}
